/**
 * Infobulle — un texte court qui se pose à côté de ce qu'il explique, au
 * survol et au focus clavier, dans le thème de l'endroit. À la place des
 * 1 256 `title=` du cockpit (la bulle de l'OS : lente, hors du thème,
 * invisible au clavier).
 *
 *   <Infobulle texte="Range chaque compte par visibilité"><Button …>Aiguilleur</Button></Infobulle>
 *   <Infobulle texte="Supprimer" cote="bas"><Button iconOnly …><Trash2 /></Button></Infobulle>
 *
 * UN enfant, qui reçoit les gestes (`onPointerEnter/Leave`, `onFocus/Blur`)
 * et `aria-describedby` quand la bulle est là — les siens sont gardés. La
 * bulle est portée sur `<body>` (au-dessus de tout) mais recopie le thème de
 * l'ancêtre (cf portail.ts), se pose en haut par défaut, change de côté si la
 * place manque, ne sort jamais de l'écran ; Échap et un défilement la
 * ferment. Lot 2 de la refonte du catalogue, 28-10-2026.
 */
import {
  Children,
  cloneElement,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent,
  type PointerEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
} from "react";
import { createPortal } from "react-dom";

import { cadreDuTheme, rectVue, vueDuCorps, type CadreTheme } from "./portail";
import { cn } from "./utils";
import "./lecture.css";

export type CoteInfobulle = "haut" | "bas" | "gauche" | "droite";

/** Ce que l'enfant peut déjà porter, et qu'on garde. */
interface PropsDeLAncre {
  ref?: Ref<HTMLElement>;
  onPointerEnter?: (e: PointerEvent<HTMLElement>) => void;
  onPointerLeave?: (e: PointerEvent<HTMLElement>) => void;
  onFocus?: (e: FocusEvent<HTMLElement>) => void;
  onBlur?: (e: FocusEvent<HTMLElement>) => void;
  "aria-describedby"?: string;
}

export interface InfobulleProps {
  texte: ReactNode;
  /** Où elle se pose : `haut` (défaut), `bas`, `gauche`, `droite`. */
  cote?: CoteInfobulle;
  /** Le délai avant qu'elle apparaisse, en ms (défaut 350). */
  delai?: number;
  children: ReactElement<PropsDeLAncre>;
  className?: string;
}

const ECART = 6;
const MARGE = 8;

export function Infobulle({ texte, cote = "haut", delai = 350, children, className }: InfobulleProps) {
  const id = useId();
  const ancre = useRef<HTMLElement | null>(null);
  const bulle = useRef<HTMLDivElement>(null);
  const minuterie = useRef<number | undefined>(undefined);
  const [visible, setVisible] = useState(false);
  const [cadre, setCadre] = useState<CadreTheme>({ sombre: false });
  const [pos, setPos] = useState<{ left: number; top: number; cote: CoteInfobulle } | null>(null);

  function montrer() {
    window.clearTimeout(minuterie.current);
    minuterie.current = window.setTimeout(() => {
      setCadre(cadreDuTheme(ancre.current));
      setPos(null);
      setVisible(true);
    }, delai);
  }
  function cacher() {
    window.clearTimeout(minuterie.current);
    setVisible(false);
  }
  useEffect(() => () => window.clearTimeout(minuterie.current), []);

  // Du côté demandé ; de l'autre si la place manque ; jamais hors de l'écran.
  useLayoutEffect(() => {
    if (!visible) return;
    const { zoom, largeur: W, hauteur: H } = vueDuCorps();
    const a = rectVue(ancre.current, zoom);
    const b = rectVue(bulle.current, zoom);
    if (!a || !b) return;
    let c = cote;
    if (c === "haut" && a.top - ECART - b.height < MARGE) c = "bas";
    else if (c === "bas" && a.bottom + ECART + b.height > H - MARGE) c = "haut";
    else if (c === "gauche" && a.left - ECART - b.width < MARGE) c = "droite";
    else if (c === "droite" && a.right + ECART + b.width > W - MARGE) c = "gauche";
    let left: number;
    let top: number;
    if (c === "haut" || c === "bas") {
      left = a.left + a.width / 2 - b.width / 2;
      top = c === "haut" ? a.top - ECART - b.height : a.bottom + ECART;
    } else {
      left = c === "gauche" ? a.left - ECART - b.width : a.right + ECART;
      top = a.top + a.height / 2 - b.height / 2;
    }
    setPos({
      left: Math.max(MARGE, Math.min(left, W - b.width - MARGE)),
      top: Math.max(MARGE, Math.min(top, H - b.height - MARGE)),
      cote: c,
    });
  }, [visible, cote]);

  useEffect(() => {
    if (!visible) return;
    const auClavier = (e: KeyboardEvent) => {
      if (e.key === "Escape") cacher();
    };
    const fermer = () => cacher();
    window.addEventListener("keydown", auClavier);
    window.addEventListener("scroll", fermer, true);
    window.addEventListener("resize", fermer);
    return () => {
      window.removeEventListener("keydown", auClavier);
      window.removeEventListener("scroll", fermer, true);
      window.removeEventListener("resize", fermer);
    };
  }, [visible]);

  const enfant = Children.only(children);
  const siens = enfant.props;
  const clone = cloneElement(enfant, {
    ref: (node: HTMLElement | null) => {
      ancre.current = node;
      const r = siens.ref;
      if (typeof r === "function") r(node);
      else if (r && typeof r === "object") (r as { current: HTMLElement | null }).current = node;
    },
    onPointerEnter: (e: PointerEvent<HTMLElement>) => {
      siens.onPointerEnter?.(e);
      montrer();
    },
    onPointerLeave: (e: PointerEvent<HTMLElement>) => {
      siens.onPointerLeave?.(e);
      cacher();
    },
    onFocus: (e: FocusEvent<HTMLElement>) => {
      siens.onFocus?.(e);
      montrer();
    },
    onBlur: (e: FocusEvent<HTMLElement>) => {
      siens.onBlur?.(e);
      cacher();
    },
    "aria-describedby": visible ? [siens["aria-describedby"], id].filter(Boolean).join(" ") : siens["aria-describedby"],
  });

  return (
    <>
      {clone}
      {visible &&
        createPortal(
          <div className={cadre.sombre ? "dark" : undefined} data-theme={cadre.theme}>
            <Bulle
              ref={bulle}
              id={id}
              cote={pos?.cote ?? cote}
              className={className}
              style={pos ? { left: pos.left, top: pos.top } : { left: 0, top: 0, visibility: "hidden" }}
            >
              {texte}
            </Bulle>
          </div>,
          document.body,
        )}
    </>
  );
}

/** La bulle seule. `statique` la pose dans la page (le catalogue, qui ne survole rien). */
export function Bulle({
  ref,
  id,
  cote = "haut",
  statique = false,
  style,
  className,
  children,
}: {
  ref?: Ref<HTMLDivElement>;
  id?: string;
  cote?: CoteInfobulle;
  statique?: boolean;
  style?: CSSProperties;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div ref={ref} id={id} role="tooltip" data-cote={cote} className={cn("ib lc-bulle", statique && "ib-statique", className)} style={style}>
      {children}
    </div>
  );
}
